import { Ref, ref, UnwrapNestedRefs } from 'vue'
import { approvalsApi } from '@/api'
import { useI18n } from 'vue-i18n'
import useToasts from './useToasts'
import useApprovalHistory from './useApprovalHistory'
import usePhrases from './usePhrases'
import useDocuments from './useDocuments'
import { ActivityActions } from '@/composables/useActivityLogs'
import { CanonicalsConfigObject, PhrasePointObject, PropItems } from '@coac-gmbh/saifty-phrases'
import { applyAIRulesToTheConfidenceField, SDS_CLASSIFICATION_CANONICAL_LIST } from '@/config'
import {
  CanonicalAction,
  DataObjectData,
  DataObjectDTO,
  DataPointLocation,
  Props,
  Usages,
} from '@coac-gmbh/saifty-main-apis'
import { getCanonicalNameWithoutNumberSuffix } from '@/composables/useValidationItem'
import useRolesAndPermissions from '@/composables/useRolesAndPermissions'

interface ToggleConfig {
  dataObject: DataObjectDTO
  item: DataObjectData
  cardValue: string
  phrases: PhrasePointObject[]
  newLocation?: DataPointLocation
  isSapMode?: boolean
}

export interface DataPointExternalControls {
  itemName: string
  isVisible: boolean
}

export interface SectionExternalControls {
  sectionId: string
  isVisible: boolean
}

export interface LoadDocumentConfig {
  createMissingPassportItems?: boolean
  deletedItem?: string
  force?: boolean
}

export enum FILTER_TYPES {
  All = 'All items',
  Open = 'Not approved',
  NoPhrasesFound = `With 'no phrases found'`,
}

export enum ApprovalModes {
  Default = 'Default',
  Advanced = 'Advanced',
  SAP = 'SAP',
}

export enum TabSectionsEnum {
  SAPTools = 'SAPTools',
  References = 'References',
}

export const APPROVAL_TIMESTAMP_ITEM_FORMAT = 'MM.DD.YYYY, HH:mm:ss'

export function formatCanonicalByCanonicalPreffixAndNumber(
  canonicalSectionPrefix: string,
  canonicalPreffix: string,
  suffixNumber: number,
): string {
  return `${canonicalSectionPrefix}.${canonicalPreffix}_${suffixNumber}`
}

export function getNewDefaultItem(itemName?: string): DataObjectData {
  return {
    fieldName: itemName || '',
    fieldValue: '',
    confidence: 0,
    location: [],
    action: CanonicalAction.Extracted,
    props: [],
  }
}

export function getDefaultProp(data: Partial<Props>): Props {
  return {
    sort: data.sort || 0,
    sequence: data.sequence || 0,
    source: data.source || '',
    sourceNote: data.sourceNote || '',
    recordNo: data.recordNo || '',
    isReferenced: data.isReferenced || false,
    isInherited: data.isInherited || false,
    usages: data.usages || [],
    propItems: data.propItems || [],
  }
}

export function getNewDefaultPropItem(data: Partial<PropItems>): PropItems {
  return {
    subCanonicalName: data.subCanonicalName || '',
    sort: data.sort || 1,
    propName: data.propName || '',
    propDesc: data.propDesc || '',
    propPhraseKey: data.propPhraseKey || '',
    propPhraseLib: data.propPhraseLib || '',
    subCharCategory: data.subCharCategory || '',
    confidence: data.confidence || 100,
    propTitle: data.propTitle,
    propPhraseCode: data.propPhraseCode || '',
    propPhraseGroup: data.propPhraseGroup || '',
    isPhrase: data.isPhrase || false,
    propValue: data.propValue || '',
  }
}

export function getNewDefaultUsage(value?: string): Usages {
  return {
    rating: 'PUBLIC',
    excl_ind: false,
    validity_area: value || '',
    act_ind: true,
    rel_ind: false,
  }
}

export function isClassificationItem(fieldName: string) {
  return SDS_CLASSIFICATION_CANONICAL_LIST.find(
    (i) => fieldName && i === getCanonicalNameWithoutNumberSuffix(fieldName),
  )
}

export default function useApprovals(
  documentComposable: ReturnType<typeof useDocuments>,
  phrasesComposable: UnwrapNestedRefs<ReturnType<typeof usePhrases>>,
  canonicalsConfig: Ref<CanonicalsConfigObject[] | undefined>,
) {
  const { t } = useI18n()
  const { addToast } = useToasts()
  const { formatActivityLogToStoreInApprovalMetadata } = useApprovalHistory()
  const { Permissions, hasPermission } = useRolesAndPermissions()
  const isVeryfing = ref(false)
  const verifyingCanonical = ref<string | undefined>()
  const currentApprovalTab = ref<TabSectionsEnum>(TabSectionsEnum.SAPTools)
  const approvalGeneralTabsSections = [
    {
      label: t('SAP Tools'),
      id: TabSectionsEnum.SAPTools,
    },
    { label: t('References'), id: TabSectionsEnum.References },
  ].filter((section) => {
    if (section.id === TabSectionsEnum.References && hasPermission(Permissions.AddReferencesToMaterials)) {
      return true
    }
    if (section.id === TabSectionsEnum.SAPTools) {
      return true
    }

    return false
  })

  // We only return the value 'true' in the event that a validation fails, and we should not display the failure request message.
  async function toggleItemProvenance(config: ToggleConfig) {
    if (!config.dataObject.dataAssetsId || !config.dataObject.id) {
      throw new Error()
    }

    isVeryfing.value = true
    verifyingCanonical.value = config.item.fieldName

    const isItemValidated = config.item?.action === CanonicalAction.Verified

    // TODO: Emit event to change phrases mode to false
    // if (!props.isSapMode) changePhrasesMode.value = false
    try {
      const newItemData: DataObjectData = config.item
        ? { ...config.item }
        : {
            fieldName: '',
            fieldValue: '',
            action: CanonicalAction.Extracted,
          }
      newItemData.action = isItemValidated ? CanonicalAction.Extracted : CanonicalAction.Verified

      applyAIRulesToTheConfidenceField(isItemValidated, newItemData, config.cardValue)

      if (config.newLocation && config.newLocation.page && config.newLocation.bounds)
        newItemData.location = [config.newLocation]
      if (config.item.fieldValue !== config.cardValue) newItemData.fieldValue = config.cardValue

      await approvalsApi.createOrUpdateItemsInApproval({
        dataAssetId: config.dataObject.dataAssetsId,
        dataObjectId: config.dataObject.id,
        dataObjectData: [newItemData],
      })
      config.item.action = newItemData.action

      if (documentComposable) {
        documentComposable.storeMetadata({
          newActivityLog: formatActivityLogToStoreInApprovalMetadata(ActivityActions.Update, [
            {
              object: config.item,
              objectChanges: newItemData,
            },
          ]),
        })
      }

      if (config.isSapMode) {
        afterSave(config.item, config.cardValue, config.phrases)
      }

      afterSaveForSpecialItems(config.item, config.cardValue)

      // TODO: Emit event
      // emits('updated:item')
      addToast({ text: t('Item status changed'), variant: 'success' })
    } catch {
      throw new Error()
    }

    verifyingCanonical.value = undefined
    isVeryfing.value = false
  }

  // We need to use the local phrases, for this reason we don't use the phrases of the phrases composable
  async function afterSave(item: DataObjectData, cardValue: string, phrases: PhrasePointObject[]) {
    const isItemWithSinglePhraseForSAP = phrasesComposable?.isItemWithSinglePhraseForSAP(item.fieldName || '') ?? true

    if (isItemWithSinglePhraseForSAP && phrases.length > 0 && cardValue) {
      // These items has not phrases logic, but we need to save the text in the single phrase item because that's used for SAP
      phrases[0].phrase_txt = cardValue
    }

    phrasesComposable?.updateItemPhrases(item.fieldName || '', phrases)
  }

  // Special items like 'Name' or 'CAS' need to update the phrase value in order to keep update the latest value in the SAP pages
  async function afterSaveForSpecialItems(item: DataObjectData, cardValue: string) {
    const isItemWithSinglePhraseForSAP = phrasesComposable?.isItemWithSinglePhraseForSAP(item.fieldName || '') ?? true
    const currentItemConfig = getCurrentItemConfig(item.fieldName || '')
    const phrases = phrasesComposable?.getPhrasesForItem(item.fieldName || '') || []

    const isSAPItem = currentItemConfig?.flag_sap === 1 || currentItemConfig?.flag_sap === 1.0
    const needUpdatePhraseValue = currentItemConfig?.flag_lit_props === 0 || currentItemConfig?.flag_lit_props === 0.0

    if (isItemWithSinglePhraseForSAP && phrases.length > 0 && isSAPItem && needUpdatePhraseValue && item.fieldName) {
      // These items has not phrases logic, but we need to save the text in the single phrase item because that's used for SAP
      phrases[0].phrase_txt = cardValue
      // We need to update also the content_subset
      phrases[0].content_subset = cardValue
      phrasesComposable?.updateItemPhrases(item.fieldName, phrases)
    }
  }

  function getCurrentItemConfig(canonicalName: string) {
    return (canonicalsConfig.value || []).find((c) => c.canonical_name === canonicalName)
  }

  return { isVeryfing, verifyingCanonical, currentApprovalTab, approvalGeneralTabsSections, toggleItemProvenance }
}
